import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';

import useInView from 'hooks/useInView';
import useIsFirstRender from 'hooks/useIsFirstRender';
import { useBlocker } from 'hooks/usePrompt';

import { openVideo, resetVideoPopup, setVideoPopup } from 'stores/videoSlice';

import { faMinimize, faPlay } from '@fortawesome/pro-solid-svg-icons';

import Image from './Image';
import Modal from './Modal';
import Typography from './Typography';
import YoutubePlayer from './YoutubePlayer';
import { ModalContent, ModalTitle } from './styled/Modal.styled';
import * as S from './styled/Video.styled';

/**
 * @description Base video component
 */
const VideoComponent = ({
	logo,
	title,
	controls,
	autoplay,
	videoId,
	volume,
	setProgress: setProgressModal,
	onEnded = () => {}
}) => {
	const dispatch = useDispatch();
	const containerRef = useRef(null);

	// With the firstplay state we can reset popup video
	const isFirstRender = useIsFirstRender();

	// Is video playing
	const [playing, setPlaying] = useState(false);
	// How many seconds the video played
	const [progress, setProgress] = useState(0);

	const videoRedux = useSelector((state) => state.videoPopup);
	const { progress: initialProgress } = videoRedux;

	const isIntersecting = useInView(containerRef);

	const onPopupTrigger = () => {
		dispatch(
			setVideoPopup({
				controls,
				title,
				logo,
				popup: true,
				autoplay: true,
				progress,
				videoId,
				volume
			})
		);
	};
	const onProgress = (progress) => {
		setProgress(progress);
		setProgressModal && setProgressModal(progress);
	};
	const onPlay = () => setPlaying(true);
	const onPause = () => setPlaying(false);

	// we gotta remove the video popup when the component is unmounted just once
	useEffect(() => {
		isFirstRender && dispatch(resetVideoPopup());
	}, [isFirstRender]);

	// if the video is playing and not intersecting, we should open popup
	useEffect(() => {
		playing && !isIntersecting && onPopupTrigger();
		playing && isIntersecting && dispatch(openVideo());
	}, [isIntersecting]);

	// if route changes we should open popup - For now we don't need this
	useBlocker((tx) => {
		playing && onPopupTrigger();
		tx.retry();
	});

	return (
		<div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
			<YoutubePlayer
				videoId={videoId}
				onProgress={onProgress}
				onPlay={onPlay}
				onPause={onPause}
				onEnded={onEnded}
				progress={initialProgress}
				autoplay={autoplay}
				controls={controls}
				volume={volume}
			/>
		</div>
	);
};

/**
 * @description Video modal
 */
export const VideoModal = NiceModal.create(({ title, logo, videoId, volume, controls, progress: initialProgress }) => {
	const [progress, setProgress] = useState(0);

	const dispatch = useDispatch();
	const modal = useModal();
	const { t } = useTranslation();

	const modalInfos = {
		controls,
		title,
		logo,
		popup: true,
		autoplay: true,
		progress,
		videoId,
		volume
	};

	// if we click the close button, we should reset the video popup
	const modalOnClose = () => {
		dispatch(resetVideoPopup());
	};

	// if we click the minimize button, we should set the video popup
	const minimizeHandler = () => {
		modal.remove();
		dispatch(setVideoPopup(modalInfos));
	};

	return (
		<Modal fullwidth maxWidth="large" onClose={modalOnClose} className="modal-videoPopComponent">
			<ModalTitle>
				<S.Header className="mt-1">
					<Image url={logo} style={{ borderRadius: '50%' }} />
					<div className="mt-2">
						<Typography fontWeight="500" noWrap>
							{title}
						</Typography>
						<Typography
							color="var(--blue)"
							style={{ cursor: 'pointer' }}
							onClick={() => window.open(t('helpCenterLink'))}>
							popup.seeMoreVideos
						</Typography>
					</div>
				</S.Header>
			</ModalTitle>
			<ModalContent style={{ overflow: 'hidden', minHeight: '50rem' }}>
				<VideoComponent setProgress={setProgress} progress={initialProgress} {...modalInfos} />
			</ModalContent>
			<S.ModalBottom>
				<Button
					className="m-0"
					onClick={minimizeHandler}
					variant="borderless"
					icon={faMinimize}
					iconSize="medium"
					iconButton
				/>
			</S.ModalBottom>
		</Modal>
	);
});

/**
 * @author @buunsal
 * @description VideoPopComponent is a component that is used to display a video relative and poppedup.
 * @param {"popup" | "inline"} type - Video type
 * @param {string} title - Video title
 * @param {string} overlayImage - Video overlay image
 * @param {string} logo - The logo will be displayed in the popup
 * @param {string} url - Video url
 * @param {boolean} initalPlaying - If video should be played when component is mounted
 * @param {string} width - Video width
 * @param {string} height - Video height
 * @param {number} volume - Video volume
 * @param {boolean} muted - If video should be muted
 * @param {boolean} controls - If video should have controls
 * @param {string} modalWidth - Video width in popup
 * @param {string} modalHeight - Video height in popup 
 * @param {object} rest - Rest of the props
 * @example
	<VideoPopComponent
	overlayImage="https://lh3.googleusercontent.com/p/AF1QipMPs6yTIKogenz8FGRiOVufab4-jiuYNtYaPHFQ=s680-w680-h510"
	url="https://www.youtube.com/watch?v=47PaH7U9WYc"
	logo="assets/images/otoLogo.png"
	title="onboard.guidance"
	width="100%"
	height="43.5rem"
	modalWidth="40rem"
	modalHeight="30rem"
	controls
	/>
 */
export const Video = (props) => {
	const { width = '50rem', height = '30rem', overlayImage, variant, aspectRatio } = props;

	// if the click the overlay, we should open the modal
	const clickHandler = () => {
		variant === 'popup' &&
			NiceModal.show(VideoModal, {
				...props
			});
	};

	return (
		<S.Container onClick={clickHandler} style={{ width, height, aspectRatio }}>
			{variant === 'popup' && (
				<>
					<Image url={overlayImage} width={width} height={height} style={{ objectFit: 'contain' }} />
					<S.OverlayContainer>
						<S.IconStyled icon={faPlay} />
					</S.OverlayContainer>
				</>
			)}
			{variant === 'inline' && <VideoComponent {...props} />}
		</S.Container>
	);
};

Video.defaultProps = {
	variant: 'popup',
	modalWidth: '30rem',
	modalHeight: '20rem'
};
